import React, { useState, useEffect } from "react";
import AddUserForm from "../components/addUserFrom";
import { useParams, useNavigate } from "react-router-dom";
import { addUser } from "../services/addUser";
import { v4 } from "uuid";
import { ref, onValue, get, getDatabase } from "firebase/database";
import { updateCustomer } from "../services/settings";
import Loader from "../components/UI/loader";
import Popup from "../components/UI/popup";
import CustomModel from "../components/UI/customModel";
import { getAllUsers } from "../services/settings";
import { Capacitor } from "@capacitor/core";

const FREE_DAILY_LIMIT = 10;
const isIOS = Capacitor.getPlatform() === "ios";

function getLocalTodayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function tsToLocalDate(ts) {
  if (!ts) return getLocalTodayIso();
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getUserType(users, email) {
  const u = users?.find((u) => u.email === email);
  return u?.userType || "free";
}

function AddUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userName: "",
    mobileNumber: "",
    count: "",
    category: "",
    estimate: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const database = getDatabase();
  const [bookingDate, setBookingDate] = useState(getLocalTodayIso);
  const [timeStamp, setTimeStamp] = useState();
  const [users, setUser] = useState();
  const email = localStorage.getItem("email");
  const ownerId = localStorage.getItem("ownerId");

  const onClose = () => {
    setOpen(false);
    navigate("/dashboard");
  };

  useEffect(() => {
    getAllUsers(setLoading, setUser, email);
  }, []);

  useEffect(() => {
    if (id && id !== "0") {
      setLoading(true);
      const cartRef = ref(database, "customers");
      const unsubscribe = onValue(cartRef, (snapshot) => {
        setLoading(false);
        const data = snapshot.val();
        if (data) {
          const projects = Object.values(data);
          const filteredCustomer = projects.filter((i) => i.id === id);
          if (filteredCustomer.length > 0) {
            setFormData({
              userName: filteredCustomer[0].userName,
              mobileNumber: filteredCustomer[0].mobileNumber,
              count: filteredCustomer[0].count,
              category: filteredCustomer[0].category,
              estimate: filteredCustomer[0].estimate,
              notes: filteredCustomer[0].notes,
            });
            setTimeStamp(filteredCustomer[0].timeStamp);
            setBookingDate(tsToLocalDate(filteredCustomer[0].timeStamp));
          }
        }
      });
      return () => unsubscribe();
    }
  }, [id]);

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const data = { ...formData, [name]: value };
    setFormData(data);

    // Returning customer lookup on mobile number
    if (name === "mobileNumber" && value.replace(/\D/g, "").length >= 10 && !data.userName) {
      try {
        const snap = await get(ref(database, "customers"));
        if (snap.exists()) {
          const all = Object.values(snap.val());
          const match = all.find(
            (c) =>
              c.mobileNumber === value &&
              (c.email === email || c.ownerId === ownerId) &&
              c.userName
          );
          if (match) {
            setFormData((prev) => ({ ...prev, userName: match.userName }));
            setIsReturning(true);
          }
        }
      } catch (_) {}
    } else if (name === "mobileNumber") {
      setIsReturning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (id !== "0") {
      const data = {
        userName: formData.userName,
        mobileNumber: formData.mobileNumber,
        count: formData.count,
        estimate: formData.estimate,
        notes: formData.notes,
        category: formData.category,
        timeStamp: new Date(bookingDate + "T00:00:00").toISOString(),
        id: id,
        email: email,
        ownerId: users?.find((u) => u.email === email)?.id || ownerId,
        status: "waiting",
      };
      updateCustomer(setLoading, data, id, setOpen);
    } else {
      setLoading(true);
      let tokenNumber = 1;
      let todayCount = 0;
      try {
        const snapshot = await get(ref(database, "customers"));
        if (snapshot.exists()) {
          const allCustomers = Object.values(snapshot.val());
          const todayStr = new Date().toDateString();
          todayCount = allCustomers.filter(
            (c) =>
              (c.email === email || c.ownerId === ownerId) &&
              new Date(c.timeStamp).toDateString() === todayStr
          ).length;
          tokenNumber = todayCount + 1;
        }
      } catch (err) {
        // fallback to 1
      }

      const userType = getUserType(users, email);
      if (!isIOS && userType === "free" && todayCount >= FREE_DAILY_LIMIT) {
        setLoading(false);
        setShowUpgrade(true);
        return;
      }

      const data = {
        userName: formData.userName,
        mobileNumber: formData.mobileNumber,
        count: formData.count,
        estimate: formData.estimate,
        notes: formData.notes,
        category: formData.category,
        timeStamp: new Date().toISOString(),
        id: v4(),
        email: email,
        ownerId: users?.find((u) => u.email === email)?.id || ownerId,
        status: "waiting",
        tokenNumber: tokenNumber,
      };
      addUser(data)
        .then(() => {
          setLoading(false);
          setOpen(true);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  return (
    <>
      <AddUserForm
        onChange={handleChange}
        handleSubmit={handleSubmit}
        formData={formData}
        id={id !== "0" ? id : false}
        isReturning={isReturning}
        bookingDate={bookingDate}
        onBookingDateChange={setBookingDate}
      />
      {loading && <Loader loading={loading} />}
      <Popup
        open={open}
        content="Data updated successfully!"
        buttonContent="Okay"
        onClose={onClose}
      />
      {!isIOS && (
      <CustomModel
        className="bg-white rounded-3xl"
        open={showUpgrade}
        showCloseIcon={false}
        closeOnOverlayClick={false}
      >
        <div className="flex flex-col items-center py-6 px-4">
          <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-yellow-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 3l3.057-3L12 3.5 15.943 0 19 3l-2 6.5L19 16l-3.057 3L12 15.5 8.057 19 5 16l2-6.5L5 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Daily Limit Reached
          </h3>
          <p className="text-sm text-gray-500 text-center mb-4">
            You've added {FREE_DAILY_LIMIT} customers today. Upgrade to Premium
            for unlimited customers per day.
          </p>
          <div className="w-full bg-gray-50 rounded-xl p-4 mb-5">
            <p className="text-xs font-semibold text-gray-700 mb-2">
              Contact us to upgrade:
            </p>
            <a
              href="mailto:info@pazhanam.com"
              className="flex items-center gap-2 text-sm text-primary font-medium mb-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              info@pazhanam.com
            </a>
            <a
              href="https://wa.me/918778796644"
              className="flex items-center gap-2 text-sm text-green-600 font-medium"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18a8 8 0 01-4.29-1.237l-.29-.174-3.07.806.82-2.99-.19-.3A7.96 7.96 0 014 12a8 8 0 1116 0 8 8 0 01-8 8z" />
              </svg>
              +91-8778796644
            </a>
          </div>
          <button
            onClick={() => {
              setShowUpgrade(false);
              navigate("/dashboard");
            }}
            className="w-full py-3 bg-primary hover:bg-primary-light rounded-xl text-white font-semibold text-sm"
          >
            Go to Dashboard
          </button>
        </div>
      </CustomModel>
      )}
    </>
  );
}

export default AddUser;
